import { HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { SelectItem } from 'primeng/api'
import { EMPTY, Observable, catchError, map } from 'rxjs'
import { RegistrationBankAccountRepositoryService } from 'src/app/data/repositories/erp/registration/financial/registration-bank-account/registration-bank-account-repository.service'
import { ToastService } from 'src/app/data/services/components/toast/toast.service'
import { ToastSeverity } from 'src/app/domain/enums/components/toast.enum'
import { ApiResponse } from 'src/app/domain/interfaces/application/api-response.interface'
import { Toast } from 'src/app/domain/interfaces/components/toast.interface'
import { AccountType } from 'src/app/domain/models/erp/company/account-type.model'
import { BankAccount } from 'src/app/domain/models/erp/registration/bank-account.model'
import { FinancialInstitution } from 'src/app/domain/models/erp/shared/financial-institution.model'
import { FormCreateBankAccount } from 'src/app/domain/models/erp/registration/form-create-bank-account'
import { BlobManagement } from 'src/app/domain/utils/classes/blob-management'
import { formatDateForISO8601, formatDateForWhatsappFormat } from 'src/app/domain/utils/functions/format-date'

@Injectable({ providedIn: 'root' })
export class RegistrationBankAccountService {
  private registrationBankAccountRepositoryService: RegistrationBankAccountRepositoryService = inject(RegistrationBankAccountRepositoryService)
  private toastService: ToastService = inject(ToastService)

  blobManagement: BlobManagement = new BlobManagement()

  public getBankAccountList(): Observable<Array<BankAccount>> {
    return this.registrationBankAccountRepositoryService.getBankAccountList().pipe(
      map((res: HttpResponse<ApiResponse<Array<BankAccount>>>) => {
        return (res.body?.data ?? []).map((item: any) => ({
          ...item,
          instructionMessages: item.instructionMessages ? item.instructionMessages.split(';') : [],
          protestType: item.protestType != null ? String(item.protestType) : null,
          negativateAgency: item.negativateAgency != null ? String(item.negativateAgency) : null,
        }))
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error.message } as Toast)
        return EMPTY
      })
    )
  }

  public updateBankAccount(formCreateBankAccount: FormCreateBankAccount): Observable<any> {
    const payload = this.buildFormData(formCreateBankAccount)

    return this.registrationBankAccountRepositoryService.updateBankAccount(payload).pipe(
      map((res: HttpResponse<ApiResponse<any>>) => res.body?.data),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error.message } as Toast)
        return EMPTY
      })
    )
  }

  public inactiveBankAccount(accountId: number): Observable<any> {
    return this.registrationBankAccountRepositoryService.inactiveBankAccount(accountId).pipe(
      map((res) => res.body),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error.message } as Toast)
        return EMPTY
      })
    )
  }

  public activeBankAccount(accountId: number): Observable<any> {
    return this.registrationBankAccountRepositoryService.activeBankAccount(accountId).pipe(
      map((res) => res.body),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error.message } as Toast)
        return EMPTY
      })
    )
  }

  public getAccountsType(): Observable<Array<SelectItem>> {
    return this.registrationBankAccountRepositoryService.getAccountsType().pipe(
      map((res) => res.body?.data?.map((item: AccountType) => ({ label: item.description, value: item.id }))),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar tipos de contas!' } as Toast)
        return EMPTY
      })
    )
  }

  public getFinancialInstitution(): Observable<Array<SelectItem>> {
    return this.registrationBankAccountRepositoryService.getFinancialInstitution().pipe(
      map((res: HttpResponse<ApiResponse<Array<FinancialInstitution>>>) => {
        return (res.body?.data ?? []).map((item: FinancialInstitution) => ({ label: `${item.code} - ${item.description}`, value: item.id }))
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar instituições financeiras!' } as Toast)
        return EMPTY
      })
    )
  }

  public getCompanyAccountOptions(showInactives: boolean = false, includeAccountPlan: boolean = false, accountTypeIds: Array<number> = []): Observable<Array<any>> {
    return this.registrationBankAccountRepositoryService.getCompanyAccountOptions(showInactives, includeAccountPlan, accountTypeIds).pipe(
      map((res: HttpResponse<ApiResponse<{ companyAccountList: Array<any> }>>) => res.body?.data?.companyAccountList ?? []),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar as contas bancárias!' } as Toast)
        return EMPTY
      })
    )
  }

  public getCompanyAccountOptionsByCompanies(companyIds: Array<number>, showInactives: boolean = false): Observable<Array<any>> {
    return this.registrationBankAccountRepositoryService.getCompanyAccountOptionsByCompanies({ companyIds, showInactives }).pipe(
      map((res: HttpResponse<ApiResponse<{ companyAccountList: Array<any> }>>) => res.body?.data?.companyAccountList ?? []),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: 'Falha ao recuperar as contas bancárias!' } as Toast)
        return EMPTY
      })
    )
  }

  public createBankAccount(formCreateBankAccount: FormCreateBankAccount): Observable<any> {
    const payload = this.buildFormData(formCreateBankAccount)

    return this.registrationBankAccountRepositoryService.createBankAccount(payload).pipe(
      map((res: HttpResponse<ApiResponse<any>>) => res.body?.data),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error.message, interpretNewLines: true } as Toast)
        return EMPTY
      })
    )
  }

  private buildFormData(form: FormCreateBankAccount): FormData {
    const payload: FormData = new FormData()
    const enableBoletoIssuance = !!form.enableBoletoIssuance
    const instructionMessages = enableBoletoIssuance ? form.instructionMessages.filter((message) => message?.trim()) : []

    payload.append('id', form.id?.toString() ?? '')
    payload.append('name', form.name ?? '')
    payload.append('accountTypeId', form.accountTypeId?.toString() ?? '')
    payload.append('financialInstitutionId', form.financialInstitutionId?.toString() ?? '')
    payload.append('accountPlanId', form.accountPlanId?.toString() ?? '')
    payload.append('coinId', form.coinId?.toString() ?? '')
    payload.append('initialBalance', form.initialBalance?.toString() ?? '')
    payload.append('agreementNumber', form.agreementNumber?.toString() ?? '')
    payload.append('pixTypeKeyId', form.pixTypeKeyId?.toString() ?? '')
    payload.append('pixKey', form.pixKey?.toString() ?? '')
    payload.append('agency', form.agency?.toString() ?? '')
    payload.append('agencyDigit', form.agencyDigit?.toString() ?? '')
    payload.append('accountNumber', form.accountNumber?.toString() ?? '')
    payload.append('accountDigit', form.accountDigit?.toString() ?? '')
    payload.append('initialDate', form.initialDate ? formatDateForISO8601(form.initialDate) : '')
    payload.append('finalDate', form.finalDate ? formatDateForISO8601(form.finalDate) : '')
    payload.append('active', String(form.active))
    payload.append('automaticPayments', String(form.automaticPayments))
    payload.append('mainProvisionAccount', String(form.mainProvisionAccount))
    payload.append('enableBoletoIssuance', String(form.enableBoletoIssuance))
    payload.append('billingAgreementNumber', form.billingAgreementNumber?.toString() ?? '')
    payload.append('billingAgreementWalletVariation', form.billingAgreementWalletVariation?.toString() ?? '')
    payload.append('ourNumber', form.ourNumber?.toString() ?? '')
    payload.append('beneficiaryCode', enableBoletoIssuance ? form.beneficiaryCode?.toString() ?? '' : '')
    payload.append('responsibleCompanyId', form.responsibleCompanyId?.toString() ?? '')
    payload.append('companyCode', enableBoletoIssuance ? form.companyCode?.toString() ?? '' : '')
    payload.append('billingAgreementDescription', enableBoletoIssuance ? form.billingAgreementDescription?.toString() ?? '' : '')
    payload.append('billingAgreementWallet', enableBoletoIssuance ? form.billingAgreementWallet?.toString() ?? '' : '')
    payload.append('billingAgreementCash', enableBoletoIssuance ? form.billingAgreementCash?.toString() ?? '' : '')
    payload.append('billingAgreementCNAB', enableBoletoIssuance ? form.billingAgreementCNAB?.toString() ?? '' : '')
    payload.append('contractNumber', enableBoletoIssuance ? form.contractNumber?.toString() ?? '' : '')
    payload.append('apiId', enableBoletoIssuance ? form.apiId?.toString() ?? '' : '')
    payload.append('apiKey', enableBoletoIssuance ? form.apiKey?.toString() ?? '' : '')
    payload.append('apiSecret', enableBoletoIssuance ? form.apiSecret?.toString() ?? '' : '')
    payload.append('stationCode', enableBoletoIssuance ? form.stationCode?.toString() ?? '' : '')
    payload.append('externalCertificatePassword', enableBoletoIssuance ? form.externalCertificatePassword?.toString() ?? '' : '')
    payload.append('instructionMessages', instructionMessages.join(';'))
    payload.append('instructionsBoleto', enableBoletoIssuance ? form.instructionsBoleto?.toString() ?? '' : '')
    payload.append('additionalInformationBoleto', enableBoletoIssuance ? form.additionalInformationBoleto?.toString() ?? '' : '')
    payload.append('instructionsLocalPayment', enableBoletoIssuance ? form.instructionsLocalPayment?.toString() ?? '' : '')
    payload.append('protestType', enableBoletoIssuance ? form.protestType?.toString() ?? '' : '')
    payload.append('protestDays', enableBoletoIssuance ? form.protestDays?.toString() ?? '' : '')
    payload.append('negativateAgency', enableBoletoIssuance ? form.negativateAgency?.toString() ?? '' : '')

    if (enableBoletoIssuance && form.externalCertificate) payload.append('externalCertificate', form.externalCertificate)

    return payload
  }

  public exportExcel(): Observable<any> {
    return this.registrationBankAccountRepositoryService.exportExcel().pipe(
      map((res: HttpResponse<any>) => {
        return res.body
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.blobManagement.blobError(httpError)
        return EMPTY
      })
    )
  }

  public getLogs(offset: number, limit: number): Observable<{ logs: Array<any>; total_count: number }> {
    return this.registrationBankAccountRepositoryService.getLogs(offset, limit).pipe(
      map((res: HttpResponse<ApiResponse<{ logs: Array<any>; total_count: number }>>) => {
        return { logs: res.body?.data?.logs.map((log: any) => ({ ...log, createTimestamp: formatDateForWhatsappFormat(log.createTimestamp) })), total_count: res.body?.data?.total_count }
      }),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error.message } as Toast)
        return EMPTY
      })
    )
  }

  public getTecnospeedHealthStatus(): Observable<number> {
    return this.registrationBankAccountRepositoryService.getTecnospeedHealthStatus().pipe(
      map((res: HttpResponse<ApiResponse<{ statusCode: number }>>) => res.body?.data?.statusCode),
      catchError((httpError: HttpErrorResponse, caught: Observable<any>) => {
        this.toastService.sendToast({ severity: ToastSeverity.ERROR, message: httpError.error.message } as Toast)
        return EMPTY
      })
    )
  }
}
